import { useState, useEffect } from "react";
import TopCards from "./TopCards";
import Map from "./Map";
import { Button } from "@/components/ui";
import { submitLead } from "@/api/Services/lead";
import { SuccessModal } from "@/components";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { INDIAN_STATES, COUNTRIES } from "@/pages/Career/SpecificeFeaturedJobs/consts";

function formatProductName(name: string): string {
  if (!name) return "";
  return name.replace(/(®|™)/g, "<sup>$1</sup>");
}

const ContactDetails = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    region: "",
    state: "",  
    category: "",
    product: "",
    message: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    fetch("/data/products.json")
      .then((res) => res.json())
      .then((data) => {
        setProducts(data.products || []);
      })
      .catch((err) => console.error("Error loading JSON:", err));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  type Region = "India" | "Singapore" | "Vietnam";

const selectedCountry =
  (formData.region && COUNTRIES[formData.region]) || COUNTRIES["India"];


  const validate = () => {
  const newErrors: any = {};

  if (!formData.name.trim()) newErrors.name = "Name is required";

  if (!formData.email.trim()) newErrors.email = "Email is required";
  else if (
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
  )
    newErrors.email = "Invalid email address";

  // Region-wise mobile validation
  // Region-wise mobile validation
if (!formData.mobile.trim()) {
  newErrors.mobile = "Mobile number is required";
} else {
  if (formData.region === "India" && !/^\d{10}$/.test(formData.mobile)) {
    newErrors.mobile = "India: Mobile number must be 10 digits";
  } else if (formData.region === "Singapore" && !/^[689]\d{7}$/.test(formData.mobile)) {
    newErrors.mobile = "Singapore: Mobile must be 8 digits starting with 6, 8, or 9";
  } else if (formData.region === "Vietnam" && !/^(?:[1-9]\d{8})$/.test(formData.mobile)) {
    newErrors.mobile = "Vietnam: Mobile must be 9 digits (no leading 0)";
  } else if (!/^\d{6,15}$/.test(formData.mobile)) {
    newErrors.mobile = "Enter a valid phone number (6–15 digits)";
  }
}


  if (!formData.region) newErrors.region = "Region is required";
  if (formData.region === "India" && !formData.state) {
    newErrors.state = "State is required for India";
  }

  if (!formData.category) newErrors.category = "Category is required";
  if (!formData.product) newErrors.product = "Product is required";

  return newErrors;
};


  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  const validationErrors = validate();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setLoading(true);
  try {
    const payload = {
      ...formData, // keep state for all regions
    };

    await submitLead(payload);

    setSuccessOpen(true);
    setFormData({
      name: "",
      email: "",
      mobile: "",
      region: "",
      state: "",
      category: "",
      product: "",
      message: "",
    });
    setErrors({});
  } catch (err) {
    console.error(err);
    alert("Failed to submit. Please try again.");
  } finally {
    setLoading(false);
  }
};


  const renderError = (field: string) =>
    errors[field] ? (
      <span className="text-red-500 text-sm mt-1">{errors[field]}</span>
    ) : null;

  return (
    <>
      <div className="w-full bg-gray-50 py-10">
        <TopCards />
        <div className="max-w-6xl mx-auto px-6 mb-10 mt-20">
          <div className="grid grid-cols-1 gap-6 items-stretch">
            <div
              className="shadow rounded-xl p-6 h-full flex flex-col justify-between 
                         transition-all duration-300"
              style={{
                background:
                  "linear-gradient(309.45deg, rgba(0, 167, 89, 0.08) 24.42%, rgba(0, 163, 212, 0.08) 69.56%)",
              }}
            >
              <div>
                <h3 className="text-lg font-semibold mb-6">Fill Out this Form</h3>
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 md:mr-20"
                >
                  {/* Name */}
                  <div className="flex flex-col">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Name *"
                      className={`w-full h-12 border bg-white border-gray-300 rounded-lg px-4 py-2 
                                  focus:ring-2 focus:ring-orange-500 focus:outline-none 
                                  transition hover:border-orange-400 cursor-pointer ${
                                    errors.name ? "border-red-500" : ""
                                  }`}
                    />
                    {renderError("name")}
                  </div>

                  {/* Region Dropdown */}
                  {/* Region Dropdown */}
<div className="flex flex-col">
  <Select
    value={formData.region}
    onValueChange={(val) =>
      setFormData((prev) => ({ ...prev, region: val, state: "" })) // reset state when region changes
    }
  >
    <SelectTrigger
      className={`w-full h-12 rounded-lg border border-gray-300 bg-white px-4 focus:ring-2 focus:ring-orange-500 focus:outline-none resize-y 
        transition hover:border-orange-400 cursor-pointer ${
          errors.region ? "border-red-500" : ""
        }`}
    >
      <SelectValue placeholder="Region" />
    </SelectTrigger>
    <SelectContent className="max-h-60 overflow-y-auto">
  {Object.keys(COUNTRIES).map((country) => (
    <SelectItem key={country} value={country}>
      <div className="flex items-center gap-2">
        <img
          src={COUNTRIES[country].flag}
          alt={`${country} Flag`}
          className="w-5 h-3 object-cover"
        />
        {country}
      </div>
    </SelectItem>
  ))}
</SelectContent>

  </Select>
  {renderError("region")}
</div>

{/* Conditionally render State Dropdown if India is selected */}
{formData.region === "India" && (
  <div className="flex flex-col">
    <Select
      value={(formData as any).state || ""}
      onValueChange={(val) =>
        setFormData((prev) => ({ ...prev, state: val }))
      }
    >
      <SelectTrigger
        className={`w-full h-12 rounded-lg border border-gray-300 bg-white px-4 focus:ring-2 focus:ring-orange-500 focus:outline-none resize-y 
          transition hover:border-orange-400 cursor-pointer`}
      >
        <SelectValue placeholder="Select State" />
      </SelectTrigger>
      <SelectContent className="max-h-60 overflow-y-auto">
        {INDIAN_STATES.map((st) => (
          <SelectItem key={st} value={st}>
            {st}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)}

                  {/* Mobile */}
                  <div className="flex flex-col">
    <div className="flex">
      <span className="inline-flex items-center gap-2 px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600">
        <img
          src={selectedCountry.flag}
          alt={`${formData.region || "India"} Flag`}
          className="w-5 h-3 object-cover"
        />
        {selectedCountry.code}
      </span>
      <input
        type="tel"
        name="mobile"
        value={formData.mobile}
        onChange={handleChange}
        placeholder="Enter Phone no."
        className={`w-full h-12 bg-white border border-gray-300 rounded-r-lg px-4 py-2 
          focus:ring-2 focus:ring-orange-500 focus:outline-none 
          transition hover:border-orange-400 cursor-pointer ${
            errors.mobile ? "border-red-500" : ""
          }`}
      />
    </div>
    {renderError("mobile")}
  </div>

                  {/* Email */}
                  <div className="flex flex-col">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email ID"
                      className={`w-full h-12 bg-white border border-gray-300 rounded-lg px-4 py-2 
                              focus:ring-2 focus:ring-orange-500 focus:outline-none 
                              transition hover:border-orange-400 cursor-pointer ${
                                errors.email ? "border-red-500" : ""
                              }`}
                    />
                    {renderError("email")}
                  </div>

                  {/* Category Dropdown */}
                  <div className="flex flex-col">
                    <Select
                      value={formData.category}
                      onValueChange={(val) =>
                        setFormData((prev) => ({ ...prev, category: val }))
                      }
                    >
                      <SelectTrigger
                        className={`w-full h-12 rounded-lg border border-gray-300 bg-white px-4 focus:ring-2 focus:ring-orange-500 focus:outline-none resize-y 
                               transition hover:border-orange-400 cursor-pointer ${
                                 errors.category ? "border-red-500" : ""
                               }`}
                      >
                        <SelectValue placeholder="Animal Category" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value="Poultry">Poultry</SelectItem>
                        <SelectItem value="Shrimp">Shrimp</SelectItem>
                        <SelectItem value="Fish">Fish</SelectItem>
                        <SelectItem value="Snail">Snail</SelectItem>
                        <SelectItem value="Ruminant">Ruminant</SelectItem>
                      </SelectContent>
                    </Select>
                    {renderError("category")}
                  </div>

                  {/* Product Dropdown */}
                  {/* Product Dropdown */}
<div className="flex flex-col">
  <Select
    value={formData.product}
    onValueChange={(val) =>
      setFormData((prev) => ({ ...prev, product: val }))
    }
  >
    <SelectTrigger
      className={`w-full h-12 rounded-lg border border-gray-300 bg-white px-4 focus:ring-2 focus:ring-orange-500 focus:outline-none resize-y 
             transition hover:border-orange-400 cursor-pointer ${
               errors.product ? "border-red-500" : ""
             }`}
    >
      <SelectValue placeholder="Product" />
    </SelectTrigger>
    <SelectContent className="max-h-60 overflow-y-auto">
      {products
        .filter((product) => product.brand === formData.category && product.nameId !== "rapigrow-me") // filter by selected category
        .sort((a, b) => a.name.localeCompare(b.name)) // sort alphabetically
        .map((product, idx) => (
          <SelectItem key={idx} value={product.name}>
            <span
              dangerouslySetInnerHTML={{
                __html: formatProductName(product.name),
              }}
            />
          </SelectItem>
        ))}
    </SelectContent>
  </Select>
  {renderError("product")}
</div>


                  {/* Message */}
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows={5}
                    className="md:col-span-2 w-full border bg-white border-gray-300 rounded-lg px-4 py-2 
                               focus:ring-2 focus:ring-orange-500 focus:outline-none resize-y 
                               transition hover:border-orange-400"
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="cursor-pointer w-40 mt-5 bg-primary text-white font-semibold py-5 px-10 
                               rounded-full transition-all duration-300 hover:bg-orange-500 hover:scale-105 disabled:opacity-50"
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Map />
      <SuccessModal open={successOpen} onClose={() => setSuccessOpen(false)} />
    </>
  );
};

export default ContactDetails;
